/**
 * New Year cut files — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearCutFilesPack: ToilePack = {
    slug: 'new-year-cut-files',
    category: 'season',
    name: 'New Year cut files',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'firework', label: 'Firework burst', subject: 'A firework burst with trailing sparks as one solid cut shape with clean edges, isolated on white'},
        {key: 'cheers', label: 'Lettered cheers', subject: 'The word "CHEERS" in bold connected script as one solid cut shape with clean edges, isolated on white'},
        {key: 'bottle', label: 'Bottle silhouette', subject: 'A champagne bottle with a flying cork as one solid cut shape with clean edges, isolated on white'},
    ],
    styles: [
        {key: 'deco', label: 'Art deco', style: 'art deco illustration, geometric fans and sunbursts, clean lines, no outlines', palette: 'black and gold with a little cream', framing: 'Centered on a solid black background with an even margin'},
        {key: 'neon', label: 'Neon party', style: 'neon party graphic, glowing lines, confetti and streamers', palette: 'electric pink, cyan and gold on deep navy', framing: 'Centered on a dark background, built to read clean at small size'},
        {key: 'minimal', label: 'Minimal', style: 'modern minimal illustration, few flat shapes, generous space', palette: 'gold and white with a single black', framing: 'Centered on a plain white background with generous space around it'},
    ],
    directions: ['a clock silhouette instead', 'as a layered design', 'sized for a small vinyl decal'],
    directionHint: 'try: a clock silhouette instead',
    seo: {
        title: 'New Year SVG Cut File: Firework, Cheers, Bottle',
        metaDescription: 'Nine new year cut files — firework burst, lettered cheers, champagne bottle silhouette — one colour with clean edges, three styles.',
        h1: 'New year cut file: firework, cheers, champagne bottle',
        tagline: 'Nine cut files, three midnight subjects, three styles each.',
        intro: 'Single-colour, clean-edge artwork built to cut: a firework burst, a lettered cheers, a champagne bottle silhouette, each with the solid shapes and clean edges a cutting machine needs. Ask for a clock silhouette, a confetti spray, or your own subject, and the same three styles carry it.',
        useCases: [
            'a vinyl decal for a tumbler or cup',
            'a shirt or tote bag iron-on',
            'a stencil cut for a sign or wall',
            'a card or scrapbook die-cut',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine single-colour designs — a firework burst, a lettered cheers, a champagne bottle silhouette — with clean edges, in three styles.'},
            {q: 'Do I get an actual SVG file?', a: "The pack renders the artwork as an image; converting a clean single-colour render to an SVG is a quick pass through your cutting software's trace tool."},
            {q: 'Will this work in Cricut Design Space or Silhouette Studio?', a: "The single-colour, clean-edge style is built for tracing into these tools — check your specific software's import steps for its own file requirements."},
            {q: 'Can I get a different subject, like a clock?', a: 'Yes — the direction field takes any new year subject, including a clock silhouette or a confetti spray.'},
            {q: 'Can I sell items I cut using this design?', a: 'Yes — check the license terms on your account for the specifics of commercial use.'},
        ],
    },
}
